import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import tokenUploadService from '../services/TokenUploadService';
import { theme } from '../styles/GlobalStyle';

const UploaderContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: ${theme.textPrimary};
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${theme.textPrimary};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 2px solid ${theme.borderLight};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 2px solid ${theme.borderLight};
  font-size: 1rem;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
  }
`;

const UploadButton = styled.button`
  background: ${theme.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${theme.secondary};
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: ${theme.borderLight};
    cursor: not-allowed;
    transform: none;
  }
`;

const FileUploadContainer = styled.div`
  border: 2px dashed ${theme.borderLight};
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  
  &:hover {
    border-color: ${theme.primary};
    background: rgba(63, 140, 255, 0.05);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const PreviewImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 12px;
  margin-top: 1rem;
  border: 2px solid ${theme.borderLight};
`;

const ResultContainer = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background: #f5f7fa;
  border-radius: 8px;
  border-left: 4px solid ${theme.primary};
`;

const InstructionList = styled.ol`
  padding-left: 1.5rem;
  margin-bottom: 1rem;
`;

const Instruction = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.5;
`;

const CodeBlock = styled.pre`
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  white-space: pre-wrap;
  margin: 1rem 0;
  font-family: monospace;
`;

const CopyButton = styled.button`
  background: ${theme.secondary};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.5rem;
  
  &:hover {
    background: ${theme.primary};
  }
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${theme.borderLight};
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  border-bottom: 3px solid ${props => props.active ? theme.primary : 'transparent'};
  color: ${props => props.active ? theme.primary : theme.textSecondary};
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s;
  
  &:hover {
    color: ${theme.primary};
  }
`;

const ProxyBadge = styled.span`
  background: #4caf50;
  color: white;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  vertical-align: middle;
`;

const ErrorMessage = styled.div`
  color: ${theme.error};
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: #00c853;
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const TokenMetadataUploader = ({ issuer, currency }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [twitter, setTwitter] = useState('');
  const [telegram, setTelegram] = useState('');
  const [discord, setDiscord] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tomlInstructions, setTomlInstructions] = useState(null);
  
  const fileInputRef = useRef(null);
  
  const handleFileClick = () => {
    fileInputRef.current.click();
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIconFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Make sure we have required fields
      if (!issuer || !currency || !name) {
        throw new Error('Issuer, currency, and name are required');
      }
      
      let iconUrl = null;
      
      // Upload icon if provided
      if (iconFile) {
        iconUrl = await tokenUploadService.uploadTokenIcon(iconFile, issuer, currency);
      }
      
      // Prepare token data
      const tokenData = {
        issuer,
        currency,
        name,
        description,
        iconUrl,
        socialMedia: {
          twitter: twitter || undefined,
          telegram: telegram || undefined,
          discord: discord || undefined
        }
      };
      
      // Get TOML hosting instructions - no domain needed anymore
      const instructions = await tokenUploadService.getTomlHostingInstructions(tokenData);
      
      setTomlInstructions(instructions);
      setSuccess(true);
    } catch (err) {
      console.error('Error uploading token metadata:', err);
      setError(err.message || 'Failed to upload token metadata');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        alert('Copied to clipboard!');
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };
  
  return (
    <UploaderContainer>
      <Title>Configure Your Token Metadata</Title>
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Token Name</Label>
          <Input 
            type="text" 
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Awesome Token"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Description</Label>
          <TextArea 
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your token and its purpose..."
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Token Icon</Label>
          <FileUploadContainer onClick={handleFileClick}>
            <p>Click or drag to upload your token icon (JPG, PNG, GIF, WebP)</p>
            <HiddenFileInput 
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.gif,.webp"
            />
            {previewUrl && <PreviewImage src={previewUrl} alt="Token icon preview" />}
          </FileUploadContainer>
        </FormGroup>
        
        <FormGroup>
          <Label>Social Media (Optional)</Label>
          <Input 
            type="text" 
            value={twitter}
            onChange={(e) => setTwitter(e.target.value)}
            placeholder="Twitter username (without @)"
            style={{ marginBottom: '0.5rem' }}
          />
          <Input 
            type="text" 
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder="Telegram group name"
            style={{ marginBottom: '0.5rem' }}
          />
          <Input 
            type="text" 
            value={discord}
            onChange={(e) => setDiscord(e.target.value)}
            placeholder="Discord invite code"
          />
        </FormGroup>
        
        <UploadButton type="submit" disabled={loading}>
          {loading ? 'Processing...' : 'Save Token Metadata'}
        </UploadButton>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Token metadata saved successfully!</SuccessMessage>}
      </form>
      
      {tomlInstructions && (
        <ResultContainer>
          <h3>TOML Configuration</h3>
          
          <div>
            <p>{tomlInstructions.message}</p>
            <p>Your token TOML is available at: <a href={tomlInstructions.serverTomlUrl} target="_blank" rel="noopener noreferrer">{tomlInstructions.serverTomlUrl}</a></p>
          </div>
          
          <h4>Your TOML File</h4>
          <CodeBlock>{tomlInstructions.tomlContent}</CodeBlock>
          <CopyButton onClick={() => copyToClipboard(tomlInstructions.tomlContent)}>
            Copy TOML Content
          </CopyButton>
          
          <h4>Icon Setup</h4>
          <InstructionList>
            {tomlInstructions.iconInstructions.map((instruction, index) => (
              <Instruction key={index}>{instruction}</Instruction>
            ))}
          </InstructionList>
          
          <p>
            Your token's metadata is automatically configured and will be properly displayed in tools like XRPL.to!
          </p>
        </ResultContainer>
      )}
    </UploaderContainer>
  );
};

export default TokenMetadataUploader; 